import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AuthService, IAuthResponseModel, IResponse } from '@aggregator/core';
import { FormBuilderService, IFormGroupConfig, UIButton, UIFormModule } from '@shared/ui';
import { catchError, EMPTY } from 'rxjs';
import { authFormConfig } from './configs';
import { MessagesService } from '@shared/modules/messages';

@Component({
  selector: 'aggregator-auth-page',
  standalone: true,
  imports: [UIButton, UIFormModule, ReactiveFormsModule],
  templateUrl: './auth.component.html',
  styleUrl: './auth.component.scss',
})
export class AuthPage implements OnInit {
  isFormSubmitting = false;

  form?: FormGroup;
  formConfig: IFormGroupConfig = authFormConfig;

  private formBuilder = inject(FormBuilderService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private destroy$ = inject(DestroyRef);
  private sanitizer = inject(DomSanitizer);
  private messageService = inject(MessagesService);

  get isFormInvalid(): boolean | undefined {
    return this.form?.invalid;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.build(this.formConfig);
  }

  setErrorsRecursively(form: FormGroup | FormArray, error: ValidationErrors): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);

      if (control instanceof FormControl) {
        control.setErrors(error);
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.setErrorsRecursively(control, error);
      }
    });
  }

  onSubmit(): void {
    if (this.isFormSubmitting || this.isFormInvalid) {
      return;
    }

    this.isFormSubmitting = true;

    const payload = {
      username: this.form?.get('username')?.value,
      password: this.form?.get('password')?.value,
    };

    this.authService
      .onLogin(payload)
      .pipe(
        takeUntilDestroyed(this.destroy$),
        catchError(() => {
          this.isFormSubmitting = false;
          this.form?.enable();
          this.messageService.showError('Неправильный логин или пароль!');
          return EMPTY;
        }),
      )
      .subscribe((res: IResponse<IAuthResponseModel>) => {
        const response = res as IResponse<IAuthResponseModel>;
        if (response?.isSuccess) {
          const value = response?.value as IAuthResponseModel;
          if (value?.resetPassword) {
            this.router.navigate(['set-password'], { queryParams: { token: value.tempToken } });
            return;
          }

          this.router.navigate(['']);
          return;
        }

        this.isFormSubmitting = false;
        this.form?.enable();
        this.messageService.showError('Неправильный логин или пароль!');
        return;
      });
  }

  onLinked(fieldName: string): void {
    const urls: Record<string, string> = {
      lawPersonalData: 'https://adilet.zan.kz/rus/docs/Z1300000094',
      lawInformatization: 'https://adilet.zan.kz/rus/docs/Z1500000418',
    };

    const safeUrl = this.sanitizer.sanitize(SecurityContext.URL, urls[fieldName]);
    if (safeUrl) {
      window.open(safeUrl, '_blank');
    }
  }
}
